/**
 * Où écrire le nom d'une zone au sol (« Écrire le nom au sol », éditeur).
 *
 * Devant sa porte, côté couloir : on lit le nom avant d'entrer. Une porte est
 * posée sur la rangée bloquante d'un mur horizontal (outil Portes) :
 * - mur du haut de la salle : la face du mur occupe la case au-dessus de la
 *   porte (y-1), le couloir commence en y-2 ;
 * - mur du bas : le couloir est juste en dessous (y+1).
 * Sans porte : au centre de la zone (le texte suivra la porte quand elle sera
 * posée, voir `isAutoCenter`).
 */

export interface Rect { x: number; y: number; width: number; height: number }
export interface ZoneLike extends Rect { name: string; type: string; properties: Record<string, unknown> }

/** Portes de la zone : liées par leur salle (`linkedBubble`), sinon posées sur son bord. */
export function doorsOfZone(zone: ZoneLike, all: readonly ZoneLike[]): ZoneLike[] {
  const doors = all.filter((d) => d.type === 'door');
  const linked = doors.filter((d) => d.properties['linkedBubble'] === zone.name);
  if (linked.length > 0) return linked;
  // Porte non liée mais posée dans le mur de la zone (bord, à 2 cases près).
  return doors.filter((d) => d.x + d.width > zone.x && d.x < zone.x + zone.width
    && d.y >= zone.y - 2 && d.y <= zone.y + zone.height + 1);
}

export function centerOf(zone: Rect): { tileX: number; tileY: number } {
  return {
    tileX: zone.x + Math.floor((zone.width - 1) / 2),
    tileY: zone.y + Math.floor((zone.height - 1) / 2),
  };
}

/** Case du nom, devant la porte (côté couloir), au milieu de la porte. */
export function spotInFrontOfDoor(zone: Rect, door: Rect): { tileX: number; tileY: number } {
  const topWall = door.y < zone.y + zone.height / 2;
  return {
    tileX: door.x + Math.floor((door.width - 1) / 2),
    tileY: topWall ? door.y - 2 : door.y + door.height,
  };
}

/** Case du nom d'une zone : devant sa première porte, sinon au centre. */
export function zoneNameSpot(zone: ZoneLike, all: readonly ZoneLike[]): { tileX: number; tileY: number } {
  const door = [...doorsOfZone(zone, all)].sort((a, b) => a.y - b.y || a.x - b.x)[0];
  return door ? spotInFrontOfDoor(zone, door) : centerOf(zone);
}

/** Le texte est-il encore à la place posée d'office (centre) ? Alors il peut suivre la porte. */
export function isAutoCenter(label: { tileX: number; tileY: number }, zone: Rect): boolean {
  const c = centerOf(zone);
  return label.tileX === c.tileX && label.tileY === c.tileY;
}
